import * as vp from 'vopay';
import { FunctionComponent, memo, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PushToCardContext, { PushToCardCtx } from '../PushToCardContext';
import AgreePADContext, { AgreePADCtx } from '../../CreditCard/Dialogue/AgreePADContext';
import AgreePADDialogue from '../../CreditCard/Dialogue/AgreePADDialogue';
import PushToCardDialogue from '../PushToCardDialogue';
import { AgreePADDialogueHandler, DialogueHandlerPadProps } from '../../CreditCard/Dialogue/types';
import useStorage from '../../../../hooks/useStorage';
import { DialogueHandlerProps, PushToCardDialogueHandler } from '../types';
import styles from './CrossRiver.module.scss';
import { useDataFetchIndicator } from '../../../../hooks';
import { useLayout } from '../../../../providers/Layout.provider';
import { useToken } from '../../../../providers/Token.provider';
import { useFlexiblePayment } from '../../../../providers/FlexiblePayment.provider';
import usePostPaylinkSetAccount from '../../../../hooks/useApi/usePostPaylinkSetAccount';
import { usePaylink } from '../../../../providers/Paylink.provider';

type FC = FunctionComponent;

export const CrossRiverMessage: FC = memo(() => {
  const { Success: isSuccess } = useParams<{ Success: string }>();
  const { t } = useTranslation('translations', { keyPrefix: 'CreditCardForm' });
  const [showPushToCardSuccessAlert, setShowPushToCardSuccessAlert] = useState(false);
  const [showPushToCardErrorAlert, setShowPushToCardErrorAlert] = useState(false);
  const [pushToCardError] = useState('');
  const [hasError, setHasError] = useState<boolean>(false);
  const [showAgreePADSuccessAlert, setShowAgreePADSuccessAlert] = useState(false);
  const [setAccountResponse, setAccount] = usePostPaylinkSetAccount({});
  const pushToCardMapResponse = JSON.parse(localStorage.getItem('pushToCardMapResponse') ?? '') as {
    Success: boolean;
    ErrorMessage: string;
    PushToCardDetails: {
      CardToken: '';
      IframeUrl: '';
    };
  };
  const state = JSON.parse(localStorage.getItem('state') ?? '') as {
    method: string;
    origin: string;
  };
  const history = useHistory();
  const storage = useStorage();
  const { setLayoutInfo } = useLayout();
  const { token, isSetByBrowser } = useToken();
  const { Token } = usePaylink();
  const { paymentMethods, paymentType, addPaymentMethod, isFlexiblePayment, partialPaymentValues, installmentDetails } =
    useFlexiblePayment();
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const [, setHasAgreed] = useState(false);

  useEffect(() => {
    setLayoutInfo(
      vp.chooseColor<string | null | undefined>('#FFD147', token?.BrandingDetails?.Branding?.PrimaryColor),
      'moon',
      '',
      80,
      20,
      0,
      true,
      false,
    );
  }, []);

  useEffect(() => {
    storage.padAgreement.clearPadInfo();
    if (setAccountResponse && setAccountResponse.Success) {
      history.push({
        pathname: '/thank-you',
        state: { isRedirect: true },
      });
    } else if (setAccountResponse && !setAccountResponse.Success) {
      history.push('/connect/transaction-error');
    }
  }, [setAccountResponse]);
  const completePaylink = () => {
    if (isFlexiblePayment) {
      if (paymentType === vp.FlexiblePaymentMethods.MultiplePaymentMethods) {
        const paymentMethodFind = paymentMethods.find((method) => method.method === vp.ConnectionTypes.DebitCard);
        addPaymentMethod({
          ...paymentMethodFind,
          amount: paymentMethodFind?.amount || 0,
          token: pushToCardMapResponse.PushToCardDetails.CardToken,
          account: '',
          method: vp.ConnectionTypes.DebitCard,
        });
        history.push('/connect/flexible-payment/multiple-payment-methods');
      }
      if (paymentType === vp.FlexiblePaymentMethods.PartialPayment) {
        setAccount({
          SingleUseToken: Token,
          Iq11Token: pushToCardMapResponse.PushToCardDetails.CardToken,
          PaymentMethod: 'debitcard',
          FlexiblePaymentOptions: {
            PaymentOption: 'partial',
            PartialPaymentDetails: partialPaymentValues,
          },
        });
      }
      if (paymentType === vp.FlexiblePaymentMethods.InstallmentPlan) {
        setAccount({
          SingleUseToken: Token,
          Iq11Token: pushToCardMapResponse.PushToCardDetails.CardToken,
          PaymentMethod: 'debitcard',
          FlexiblePaymentOptions: {
            PaymentOption: 'installments',
            InstallmentDetails: installmentDetails,
          },
        });
      }
    } else {
      setAccount({
        SingleUseToken: Token,
        PaymentMethod: 'debitcard',
        Iq11Token: pushToCardMapResponse.PushToCardDetails.CardToken,
      });
    }
  };
  useEffect(() => {
    setDataFetchingInfo(true, 'Loading', 'fullScreen');
    setHasError(false);
    if (isSuccess !== 'true') {
      history.push({
        pathname: '/connect/error',
        state: { paymentMethod: 'debitcard', origin: state?.origin, isRedirect: true, pageCount: 4 },
      });
      return;
    }

    if (state?.origin === 'token') {
      completePaylink();
    } else if (state?.origin === 'iframe') {
      storage.padAgreement.clearPadInfo();
      if (isSetByBrowser) {
        const redirectMethod = token?.RedirectMethod;
        const url = `${token?.RedirectURL || ''}?Token=${
          pushToCardMapResponse.PushToCardDetails.CardToken
        }&PaymentMethod=debitcard`;
        const Response = {
          Step: 'LINK',
          Url: url,
          Token: pushToCardMapResponse.PushToCardDetails.CardToken,
        };
        // inner and outer redirect
        window.top?.postMessage(Response, '*');
        if (window.parent && window.parent !== window.top) {
          window.parent.postMessage(Response, '*');
        }
        setTimeout(() => {
          if (isSetByBrowser) {
            if (redirectMethod === 'InnerRedirect') {
              window.location.href = url;
            } else if (redirectMethod === 'OuterRedirect') {
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              window.top!.location.href = url;
            } else {
              history.push({
                pathname: '/success',
                state: { isRedirect: true },
              });
            }
          }
        }, 300);
      } else {
        setShowPushToCardSuccessAlert(true);
      }
    }
  }, [isSuccess]);

  const PushToCard: PushToCardCtx = {
    showPushToCardSuccessAlert,
    showPushToCardErrorAlert,
    pushToCardError,
  };

  const AgreePAD: AgreePADCtx = {
    showAgreePADSuccessAlert,
  };

  const handlers = {
    handleConnectPushToCardDialogue: (handlerProps: DialogueHandlerProps<PushToCardDialogueHandler>) => {
      const { type, state: dialogState, action = 'default' } = handlerProps;

      const dialogueHandlers: PushToCardDialogueHandler = {
        connect: {
          success: {
            default: () => {
              window.location.reload();
              setShowPushToCardSuccessAlert(false);
              storage.padAgreement.clearPadInfo();
            },
          },
          fail: {
            default: () => {
              setShowPushToCardErrorAlert(false);
              storage.padAgreement.clearPadInfo();
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][dialogState];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
  };

  const handlersPad = {
    handleAgreePADDialogue: (handlerProps: DialogueHandlerPadProps<AgreePADDialogueHandler>) => {
      const { type, state: dialogState, action = 'default' } = handlerProps;

      const dialogueHandlers: AgreePADDialogueHandler = {
        agree: {
          success: {
            default: () => {
              setHasAgreed(true);
              storage.padAgreement.storePadInfo('agreed');
              setShowAgreePADSuccessAlert(false);
            },
          },
          pdf: {
            default: () => {
              setShowAgreePADSuccessAlert(false);
            },
          },
          fail: {
            default: () => {
              setShowAgreePADSuccessAlert(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][dialogState];
      const handler = dialogueState[action] || dialogueState.default;
      handler();
    },
  };

  return (
    <>
      <PushToCardContext.Provider value={PushToCard}>
        <div className={styles.container}>
          <vp.Box flx="col" width="50%">
            <vp.Box mb="md" flx="col" height="auto">
              <vp.Title title={`${t('title')}`} size="lg" bold />
              {hasError && (
                <div>
                  <p>There was an error in completing the transaction. Click the Back button and please try again.</p>
                </div>
              )}
            </vp.Box>
          </vp.Box>
        </div>
        <PushToCardDialogue handleDialogue={handlers.handleConnectPushToCardDialogue} />
      </PushToCardContext.Provider>
      <AgreePADContext.Provider value={AgreePAD}>
        <AgreePADDialogue handleDialogue={handlersPad.handleAgreePADDialogue} />
      </AgreePADContext.Provider>
    </>
  );
});
